import { isCartOrganizationStale } from '@lib/commerce/cartChanged';
import { areCartCountReadsHeld } from '@lib/commerce/cartCount';
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

/** commerce-bridge's seat change (PUT) and removal (POST `remove`). */
const LINE_WRITE = /\/cart\/([^/]+)\/line-item\/([^/]+)$/;

type ConflictRetryConfig = AxiosRequestConfig & { isConflictRetry?: boolean };

type CartRead = { body?: { cart?: { id?: string; version?: number } } };

/** The line write a 412 refused, when it is one to send again. */
const refusedLineWrite = (error: unknown) => {
  if (!axios.isAxiosError(error) || error.response?.status !== 412) return;
  const config = error.config as ConflictRetryConfig | undefined;
  const [, cartId, target] = config?.url?.match(LINE_WRITE) ?? [];
  const method = target === 'remove' ? 'post' : 'put';
  if (!cartId || config?.method !== method || config.isConflictRetry) return;
  return { config, cartId: decodeURIComponent(cartId) };
};

/**
 * Another tab can move the cart's version on without telling this one
 * (commerce-ui's tax calculation does), so a removal or seat change sent from
 * the version this tab holds is refused with a 412. Each is sent once more at
 * the version reread; `cartPathOf` gives the read for a cart, if any. Returns
 * the cleanup.
 */
export const retryLineWritesOnConflict = (
  client: AxiosInstance,
  cartPathOf: (cartId: string) => string | undefined
): (() => void) => {
  const interceptor = client.interceptors.response.use(
    undefined,
    async (error: unknown) => {
      const refused = refusedLineWrite(error);
      // A read for an org the cart has left would move it back.
      const path =
        refused && !areCartCountReadsHeld() && !isCartOrganizationStale()
          ? cartPathOf(refused.cartId)
          : undefined;
      if (!refused || !path) throw error;
      const { data } = await client.get<CartRead>(path).catch(() => {
        throw error;
      });
      const cart = data?.body?.cart;
      if (cart?.id !== refused.cartId) throw error;
      const sent = JSON.parse(refused.config.data);
      return client.request({
        ...refused.config,
        isConflictRetry: true,
        data: JSON.stringify({
          ...sent,
          body: { ...sent.body, version: cart.version },
        }),
      } as ConflictRetryConfig);
    }
  );
  return () => client.interceptors.response.eject(interceptor);
};
